import { useRouter } from "@bprogress/next";
import { useState } from "react";
import { createPath } from "@/service/routing/routing";
import { clientPost } from "@/service/client-api/client-api";
import { scrollToPos } from "@/service/scroll/scroll";

export function useStepNavigation({
  step,
  path,
  isPathCompleted,
  outroSeen,
  tokenQuery = "",
}: {
  step: RoadmapStepVM;
  path: RoadmapPathVM;
  isPathCompleted: boolean;
  outroSeen: boolean;
  tokenQuery?: string;
}): {
  isLoading: boolean;
  handleVisitFinish: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
} {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  function navigateAndScroll(url: string) {
    router.push(url);
    setTimeout(() => {
      scrollToPos({ top: 0, behavior: "instant" });
    }, 1000);
  }

  async function handleVisitFinish() {
    const currentStepIndex = path.steps.findIndex((s) => s.id === step.id);
    const isLastStep = currentStepIndex === path.steps.length - 1;

    const milestone = path.milestone;
    const area = milestone.area;
    const specialization = area.specialization;

    const summaryUrl =
      createPath("roadmap.path.summary", {
        specialization: specialization.slug,
        path: path.slug,
      }) + tokenQuery;

    if (!isPathCompleted) {
      await clientPost("roadmap.step.complete", { id: step.id });
    }

    // Case 1: Outro already seen and on last step → go straight to summary
    if (outroSeen && isLastStep) {
      navigateAndScroll(summaryUrl);
      return;
    }

    // Case 2: Not on the last step → go to the next sequential step
    if (!isLastStep) {
      const nextStep = path.steps[currentStepIndex + 1];
      navigateAndScroll(
        createPath("roadmap.step.show", {
          specialization: specialization.slug,
          path: nextStep.path.slug,
          step: nextStep.slug,
        }) + tokenQuery,
      );
      return;
    }

    // Case 3: On the last step → find first incomplete step (excluding current)
    let nextStep = path.steps.find(
      (s, index) =>
        index > currentStepIndex &&
        s.id !== step.id &&
        !s.progress?.isCompleted,
    );

    if (!nextStep) {
      nextStep = path.steps.find(
        (s) => s.id !== step.id && !s.progress?.isCompleted,
      );
    }

    if (nextStep) {
      // Case 3a: Has other incomplete steps → go to the first one
      navigateAndScroll(
        createPath("roadmap.step.show", {
          specialization: specialization.slug,
          path: nextStep.path.slug,
          step: nextStep.slug,
        }) + tokenQuery,
      );
    } else {
      // Case 3b: All steps complete → go to summary
      navigateAndScroll(summaryUrl);
    }
  }

  return {
    isLoading,
    handleVisitFinish,
    setIsLoading,
  };
}
